import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import MapWrapper from './MapWrapper';
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from "@/contexts/AuthContext";
import { useRouter } from 'next/router';
import { useToast } from "@/components/ui/use-toast";

interface Event {
  id: string;
  type: string;
  address: string;
  date: string;
  time: string;
  latitude: number;
  longitude: number;
  signupCount: number;
}

interface Location {
  name: string;
  url: string;
  position: {
    lat: number;
    lng: number;
  } | null;
  description?: string;
  address: string;
  category: string;
  events?: Event[];
}

const mapContainerStyle = {
  width: '100%',
  height: '600px',
  borderRadius: '0.5rem',
};

const defaultOptions = {
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  zoomControl: true,
  scrollwheel: true,
  disableDoubleClickZoom: true,
  maxZoom: 17, // Prevent excessive zoom that might trigger more tile loads
  styles: [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ],
  // Optimize performance by reducing API calls
  gestureHandling: 'cooperative',
  tilt: 0,
};

// Default center (Danang)
const defaultCenter = {
  lat: 16.0403175,
  lng: 108.2428722
};

interface LocationsMapProps {
  selectedCategory?: string;
}

export default function LocationsMap({ selectedCategory = "All" }: LocationsMapProps) {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const router = useRouter();
  const { toast } = useToast();

  const handleViewEvents = () => {
    if (!user) {
      router.push('/login');
    } else {
      router.push('/dashboard');
    }
  };

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        console.log('Fetching locations...');
        const response = await fetch('/api/locations');
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || 'Failed to fetch locations');
        }

        console.log('Received locations:', data);
        
        if (!Array.isArray(data)) {
          throw new Error('Invalid data format received');
        }

        setLocations(data);
      } catch (err) {
        console.error('Error fetching locations:', err);
        setError(err instanceof Error ? err.message : 'Failed to load locations');
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, []);

  if (loading) {
    return <div className="flex justify-center items-center h-[600px]">Loading locations...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-[600px]">Error: {error}</div>;
  }

  console.log('Rendering map with locations:', locations);

  return (
    <MapWrapper>
      <div className="mt-4 rounded-lg overflow-hidden border">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={13}
          center={defaultCenter}
          options={defaultOptions}
          onLoad={() => console.log('Map loaded')}
        >
          {locations
            .filter(location => selectedCategory === "All" || location.category === selectedCategory)
            .map((location, index) => {
              if (!location.position) {
                console.log('Skipping location without position:', location);
                return null;
              }
              
              return (
                <Marker
                  key={index}
                  position={location.position}
                  title={location.name}
                  onClick={() => setSelectedLocation(location)}
                />
              );
            })}

          {selectedLocation && selectedLocation.position && (
            <InfoWindow
              position={selectedLocation.position}
              onCloseClick={() => setSelectedLocation(null)}
            >
              <div className="p-2 max-w-[300px]">
                <h3 className="text-base font-semibold mb-3 text-black">{selectedLocation.name}</h3>
                <Badge variant="secondary" className="mb-3">{selectedLocation.category}</Badge>
                {selectedLocation.description && (
                  <p className="text-sm text-gray-600 mb-2">{selectedLocation.description}</p>
                )}
                <p className="text-sm text-gray-500 mb-3">{selectedLocation.address}</p>
                
                {selectedLocation.events && selectedLocation.events.length > 0 ? (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-black">Upcoming Events:</h4>
                    {selectedLocation.events.map((event) => {
                      const eventDate = new Date(event.date);
                      const formattedDate = eventDate.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      });
                      
                      return (
                        <div key={event.id} className="border rounded p-2">
                          <div className="flex justify-between items-start mb-1">
                            <div>
                              <p className="text-sm font-medium text-black">{event.type}</p>
                              <p className="text-xs text-gray-500">{formattedDate} at {event.time}</p>
                            </div>
                            <Badge variant="secondary" className="text-xs">
                              {event.signupCount} joined
                            </Badge>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => router.push(`/events/${event.id}`)}
                            >
                              Details
                            </Button>
                            {user ? (
                              <Button
                                size="sm"
                                className="flex-1"
                                onClick={async () => {
                                  try {
                                    const response = await fetch('/api/events/signup', {
                                      method: 'POST',
                                      headers: {
                                        'Content-Type': 'application/json',
                                      },
                                      body: JSON.stringify({
                                        eventId: event.id,
                                      }),
                                    });

                                    if (!response.ok) {
                                      const error = await response.json();
                                      throw new Error(error.message);
                                    }

                                    router.push(`/events/${event.id}`);
                                  } catch (error) {
                                    console.error('Error joining event:', error);
                                    toast({
                                      title: "Error",
                                      description: error instanceof Error ? error.message : "Failed to join event",
                                      variant: "destructive",
                                    });
                                  }
                                }}
                              >
                                Join
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                className="flex-1"
                                onClick={() => router.push('/login')}
                              >
                                Sign in to Join
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={handleViewEvents}
                  >
                    View All Events
                  </Button>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>
    </MapWrapper>
  );
}